import React from 'react';
import { useAssetActions } from '@/contexts/AssetActionsContext';
import { useNavigate } from '@/components/helpers/routerCompat';
import { useOrgPath } from '../helpers/OrgNavLink';
import { Formik } from 'formik';
import { Modal, Button, Space } from 'antd';
import { Form, FormItem, Input } from 'formik-antd';
import { CheckOutlined, FormOutlined } from '@ant-design/icons';
import FloatLabel from '../helpers/FloatLabel';
import api from '../api';
import { useTranslation } from 'react-i18next';
import { useSelectedAssetsState, useSelectedAssetsDispatch } from '@/contexts/SelectedAssetsContext';

const AccessGrantModal = () => {
    const { t } = useTranslation();
    const { 
        accessGrantModalOpen: visible, 
        setAccessGrantModalOpen: setVisible,
        grantAssetIds: ids 
    } = useAssetActions();
    
    const { selectedAssetIds } = useSelectedAssetsState();
    const selectedAssetDispatch = useSelectedAssetsDispatch();
    const assetIds = ids || selectedAssetIds || [];
    
    const navigate = useNavigate();
    const getPath = useOrgPath();
    
    if (!visible) return null;
    
    const titleText = assetIds.length === 0 ?
        t('create-grant', 'Create Grant') :
        assetIds.length === 1 ?
            t('create-grant-for-asset', 'Create Grant For 1 Asset') : 
            t('create-grant-for-assets', 'Create Grant For {{count}} Assets', {count: assetIds.length});
    
    return (
        <Formik
            initialValues={{
                requested_image_and_video_permission: 'original',
                requested_other_permission: 'original'
            }}
            enableReinitialize={true}
            onSubmit={(values, actions) => {
                values.asset_ids = assetIds;
                values.is_grant = true;
                
                api.post('/api/access_requests', {access_request: values}).then(res => {
                    actions.resetForm();
                    actions.setSubmitting(false);
                    
                    setVisible(false);
                    selectedAssetDispatch({type: 'selectNone'});
                    
                    // Navigate to the grant page for further editing before finalizing
                    navigate(getPath(`/access-requests/${res.data.slug}`));
                }).catch((err) => {
                    console.log(err);
                    actions.setSubmitting(false);
                });
            }}
        >
            {({values, submitForm, handleSubmit, isSubmitting}) => (
                <Modal
                    zIndex={2001}
                    onCancel={() => setVisible(false)}
                    open={visible}
                    title={<><FormOutlined/> {titleText}</>}
                    footer={
                        <Space>
                            <Button onClick={() => setVisible(false)}>
                                {t('cancel', 'Cancel')}
                            </Button>
                            <Button
                                loading={isSubmitting}
                                onClick={submitForm}
                                icon={<CheckOutlined/>}
                                type="primary"
                            >
                                {t('button-create-grant', 'Create Grant')}
                            </Button>
                        </Space>
                    }
                >
                    <Form onSubmit={handleSubmit} layout="vertical">
                        <FormItem name='requester_name' label={t('recipient-name', 'Recipient Name')} required>
                            <Input 
                                name='requester_name' 
                                size='large'
                            />
                        </FormItem>
                        
                        <FormItem name='requester_email' label={t('recipient-email', 'Recipient Email')} required>
                            <Input 
                                type='email'
                                name='requester_email' 
                                size='large'
                            />
                        </FormItem>
                        
                        <FormItem name='name' label={t('name-of-the-grant', 'Name of The Grant')} required>
                            <Input 
                                name='name' 
                                size='large'
                                placeholder={t('placeholder-name-of-the-grant', 'e.g. Marketing Campaign Assets')}
                            />
                        </FormItem>
                        
                        <FormItem name='description' label={t('description', 'Description')}>
                            <Input.TextArea 
                                name='description' 
                                rows={3}
                                placeholder={t('placeholder-grant-description', 'Optional description of the grant')}
                            />
                        </FormItem>
                    </Form>
                </Modal>
            )}
        </Formik>
    );
};

export default AccessGrantModal;